import type { Category } from '@hub/models/data';
import { HIDE_UNIT_SWITCH_CATEGORIES, INCH_COMPATIBLE_CATEGORIES } from './constants';
import { getFeatureFlags, type FeatureFlagKey } from '@hub/libs/hub-config';
import { isCategoryEnabled } from '@icommerce/categories';

export const CATEGORY_BEARINGS_IBH = [
  'sleeve_bearing',
  'sleeve_bearing_flange',
  'clearance_free_preloaded_bearings',
  'clearance_free_prestressed_bearing_with_flange',
  'thrust_washers',
  'disc_springs',
  'piston_rings',
  'clip_bearings',
  'split_bearings',
  'split_bearings_with_anti_rotation_feature',
  'easy_clip',
  'clip_in_double_flanged_bearings',
  'double_flanged_bearings',
  'flange_bearings',
  'pre_loaded_two_hole_flanged_bearings',
];

export class CategoryHelper {
  static extractCategoryIdsRecursive(category: Pick<Category, 'key' | 'children'>): string[] {
    if (!category.children) {
      return [category.key];
    }
    const children = category.children?.flatMap(child => this.extractCategoryIdsRecursive(child));
    return [category.key, ...children];
  }

  static isCategoryActive(category: Category, activeKey: string): boolean {
    return !!(
      category.key === activeKey || // is active category
      category.children?.find(subCategory =>
        // has active children
        this.isCategoryActive(subCategory, activeKey)
      )
    );
  }

  static isEnabled(keys: Array<string | undefined | null> = [], defaultValue = false) {
    const featureFlags = getFeatureFlags();
    return isCategoryEnabled(
      keys,
      new Map<FeatureFlagKey, boolean>(featureFlags.map(f => [f.name, f.value])),
      defaultValue
    );
  }
}

/**
 * Returns if the given category is compatible for showing inch products
 */
export const isCategoryInchCompatible = (categoryKey: string, parentKey?: string) =>
  INCH_COMPATIBLE_CATEGORIES.includes(`${parentKey ? `${parentKey}.` : ''}${categoryKey}`);

/**
 * Returns if the unit system switch must be hidden in given category
 * @param categoryKey
 * @param parentKey
 */
export const isCategoryMeasurementSwitchHidden = (categoryKey: string, parentKey?: string) =>
  HIDE_UNIT_SWITCH_CATEGORIES.includes(`${parentKey ? `${parentKey}.` : ''}${categoryKey}`);
